import { SITE_URL, isNoIndexPath } from '../data/seo'
import { currentFeatures } from '../data/features'
import { looksPersonal, normalizeSearchTerm } from '../core/rules/insights/searchTerms'

/**
 * Anonieme bezoekersstatistiek met Umami, zelf gehost, zonder cookies (docs/INZICHT.md §6).
 *
 * - Doet niets zonder `VITE_UMAMI_URL` + `VITE_UMAMI_SITE_ID`, met `features.analytics` uit, bij
 *   Do Not Track / Global Privacy Control, en nooit op de afgeschermde pagina's (dezelfde lijst
 *   als robots.txt: /afrekenen, /bestelling, /account, /nieuwsbrief, /be).
 * - Alleen vaste gebeurtenissen met vaste velden (`analyticsEvents`). Een waarde die naar een
 *   persoon kan wijzen (e-mail, telefoon, ordernummer, postcode, bedrag) laat de hele gebeurtenis vallen.
 * - "Betaald" meten we niet hier: de betaalpagina's tellen nooit mee. Dat komt uit de eigen bestellingen.
 */

/** De enige gebeurtenissen, met de enige velden die ze mogen hebben. */
export const analyticsEvents = {
  'mand-toegevoegd': ['product'],
  'afrekenen-gestart': [],
  'afspraak-aangevraagd': ['behandeling'],
  'huidadvies-afgerond': ['routine'],
  'nieuwsbrief-aangemeld': ['bron'],
  'kuur-bekeken': ['behandeling'],
  zoeken: ['term', 'resultaten'],
} as const satisfies Record<string, readonly string[]>

export type AnalyticsEvent = keyof typeof analyticsEvents
export type EventData<E extends AnalyticsEvent> = { [K in (typeof analyticsEvents)[E][number]]: string | number }
type Clean = Record<string, string | number>

/** Hoogstens zoveel tekens per tekstwaarde (een id of slug is korter). */
export const EVENT_VALUE_MAX = 50
/** Getallen: hele getallen tot 4 cijfers (een aantal, nooit een bedrag of nummer). */
export const EVENT_NUMBER_MAX = 9999

const AMOUNT = /€|\d+[.,]\d{2}\b/

/**
 * Puur: de gebeurtenis zoals hij naar Umami mag, of `null` als hij niet mag. Onbekende velden
 * vallen weg; een ontbrekend of verdacht veld laat de hele gebeurtenis vallen.
 */
export function cleanEventData(event: string, data: Record<string, unknown>): Clean | null {
  const fields: readonly string[] | undefined = (analyticsEvents as Record<string, readonly string[]>)[event]
  if (!fields) return null
  const out: Clean = {}
  for (const field of fields) {
    const raw = data[field]
    if (typeof raw === 'number') {
      if (!Number.isInteger(raw) || raw < 0) return null
      out[field] = Math.min(raw, EVENT_NUMBER_MAX)
      continue
    }
    if (typeof raw !== 'string') return null
    const value = field === 'term' ? normalizeSearchTerm(raw) : raw.trim().toLowerCase().slice(0, EVENT_VALUE_MAX)
    if (!value || looksPersonal(value) || AMOUNT.test(value)) return null
    out[field] = value
  }
  return out
}

/** Telt deze pagina mee? Nooit de afgeschermde delen. */
export const analyticsAllowed = (pathname: string) => !isNoIndexPath(pathname)

// ——— Configuratie ———

export type UmamiConfig = { src: string; websiteId: string; domains: string }

const readEnv = (): Record<string, string | undefined> => (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {}

/** Alleen met een https-adres (http alleen voor localhost) en een geldige site-id; anders `null`. */
export function umamiConfig(env: Record<string, string | undefined> = readEnv()): UmamiConfig | null {
  const base = env.VITE_UMAMI_URL?.trim().replace(/\/+$/, '')
  const websiteId = env.VITE_UMAMI_SITE_ID?.trim()
  if (!base || !websiteId || !/^[0-9a-f-]{36}$/i.test(websiteId)) return null
  let url: URL
  try {
    url = new URL(base)
  } catch {
    return null
  }
  const local = url.hostname === 'localhost' || url.hostname === '127.0.0.1'
  if (url.protocol !== 'https:' && !(local && url.protocol === 'http:')) return null
  const host = new URL(SITE_URL).hostname
  return { src: `${base}/script.js`, websiteId, domains: `${host},www.${host}` }
}

/** Do Not Track of Global Privacy Control aan: dan laden we niets. */
export function prefersNoTracking(): boolean {
  if (typeof navigator === 'undefined') return true
  const n = navigator as Navigator & { globalPrivacyControl?: boolean }
  const w = typeof window !== 'undefined' ? (window as Window & { doNotTrack?: string }) : undefined
  return n.doNotTrack === '1' || w?.doNotTrack === '1' || n.globalPrivacyControl === true
}

// ——— utm_source uit de adresbalk ———

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign']

/**
 * Puur: de utm-velden apart van de rest. De site leest ze één keer (eerste paginaweergave) en haalt
 * ze dan uit de adresbalk, zodat een doorgestuurde link niet ook als "flyer" telt en Google geen
 * dubbele pagina's ziet.
 */
export function splitUtm(search: string): { utm: string; rest: string } {
  const params = new URLSearchParams(search)
  const utm = new URLSearchParams()
  UTM_KEYS.forEach((k) => {
    const v = params.get(k)
    if (v && /^[a-z0-9_-]{1,40}$/i.test(v)) utm.set(k, v.toLowerCase())
    params.delete(k)
  })
  const rest = params.toString()
  return { utm: utm.toString(), rest: rest ? `?${rest}` : '' }
}

// ——— Versturen ———

type UmamiApi = { track: (eventOrFn?: string | ((props: Record<string, unknown>) => Record<string, unknown>), data?: Clean) => void }
const umami = (): UmamiApi | undefined => (typeof window === 'undefined' ? undefined : (window as Window & { umami?: UmamiApi }).umami)

let active = false
/** Gezet door AnalyticsScript zodra het script geladen is (en weer uit als het onderdeel uit gaat). */
export const setAnalyticsActive = (on: boolean) => {
  active = on
}

const canSend = () => active && currentFeatures().analytics && typeof window !== 'undefined' && analyticsAllowed(window.location.pathname)

/** Eén gebeurtenis. Doet niets zonder Umami, op een afgeschermde pagina of met een verdachte waarde. */
export function track<E extends AnalyticsEvent>(event: E, data: EventData<E>): void {
  // Alles binnen try: statistiek mag de site nooit hinderen, ook niet bij een kapotte waarde.
  try {
    if (!canSend()) return
    const clean = cleanEventData(event, data)
    if (!clean) return
    umami()?.track(event, Object.keys(clean).length ? clean : undefined)
  } catch {
    // stil
  }
}

/** Een paginaweergave (de site telt zelf, want Umami's automatische telling staat uit). */
export function trackPageview(url: string): void {
  try {
    if (!canSend()) return
    umami()?.track((props) => ({ ...props, url }))
  } catch {
    // idem
  }
}
